import type { Connector, VoltageInfo } from '@ev/shared'
import { useOverlayStore } from '../../about/overlayStore'
import { type Strings, useStrings } from '../../i18n'
import { formatPower } from '../format'
import { useLocale } from '../useLocale'
import {
  formatNumber,
  type ReadingDisplay,
  toVoltageDisplay,
  type VoltageDisplay,
  type VoltageFigure,
} from './model'

/**
 * The one component this whole site exists for.
 *
 * Three visually distinct states, in descending order of trust:
 *
 * - **declared** - a solid, filled badge. Open Charge Map published this number
 *   for the matched connector, and it is as close to measured as this data gets.
 * - **derived** - an outlined badge with a dotted underline, and a button, not
 *   a span: every derived figure is a live link into the methodology, because a
 *   number the site computed has to be able to show its working.
 * - **unknown** - the word, in muted italics. Never a plausible-looking figure,
 *   never a dash that could be mistaken for zero.
 *
 * The shapes differ, not just the colours, so the distinction survives
 * greyscale, colour blindness and a bad phone screen in sunlight.
 */

const figureText = (figure: VoltageFigure, strings: Strings, locale: string): string => {
  const unit = strings.voltage.voltUnit
  switch (figure.kind) {
    case 'range':
      return `${formatNumber(figure.minV, locale)}-${formatNumber(figure.maxV, locale)} ${unit}`
    case 'atLeast':
      return strings.voltage.atLeast(`${formatNumber(figure.volts, locale)} ${unit}`)
    case 'atMost':
      return strings.voltage.atMost(`${formatNumber(figure.volts, locale)} ${unit}`)
    default:
      return `${formatNumber(figure.volts, locale)} ${unit}`
  }
}

/** The provenance word that sits next to the figure and in the aria label. */
const provenanceLabel = (display: VoltageDisplay, strings: Strings): string => {
  if (display.provenance === 'declared') return strings.voltage.declared
  if (display.provenance === 'derived') return strings.voltage.derived
  return strings.voltage.unknown
}

export const VoltageBadge = ({ voltage }: { voltage: VoltageInfo | undefined }) => {
  const strings = useStrings()
  const locale = useLocale()
  const openOverlay = useOverlayStore((state) => state.open)
  const display = toVoltageDisplay(voltage)
  const disputed = display.alternatives.length > 0

  if (display.figure === null)
    return (
      <span className="ev-volt ev-volt--unknown" title={strings.voltage.unknownHint}>
        <span className="visually-hidden">{`${strings.voltage.heading}: `}</span>
        {strings.voltage.unknown}
      </span>
    )

  const value = figureText(display.figure, strings, locale)
  const provenance = provenanceLabel(display, strings)
  const sourceName = display.source === null ? '' : strings.voltage.source[display.source]

  // A disputed figure keeps its provenance styling - it is still a published
  // number - and gains a marker, because the badge is what a reader looks at
  // first and it must not present a contested figure as a settled one.
  const disputeMark = disputed ? (
    <>
      <span aria-hidden="true" className="ev-volt__dispute">
        ≠
      </span>
      <span className="visually-hidden">{`, ${strings.voltage.conflictTitle}`}</span>
    </>
  ) : null

  if (display.provenance === 'declared')
    return (
      <span
        className={`ev-volt ev-volt--declared${disputed ? ' ev-volt--disputed' : ''}`}
        title={
          disputed
            ? `${strings.voltage.conflictTitle} (${sourceName})`
            : `${strings.voltage.declaredHint} (${sourceName})`
        }
      >
        <span className="visually-hidden">{`${strings.voltage.heading}: `}</span>
        {value}
        {disputeMark}
        <span className="visually-hidden">{`, ${provenance}, ${sourceName}`}</span>
      </span>
    )

  return (
    <button
      type="button"
      className={`ev-volt ev-volt--derived${disputed ? ' ev-volt--disputed' : ''}`}
      onClick={() => openOverlay('methodology')}
      title={`${strings.voltage.derivedHint} (${sourceName})`}
      aria-label={`${strings.voltage.heading}: ${value}, ${provenance}, ${sourceName}. ${strings.voltage.methodologyOpen}`}
    >
      {value}
      {disputeMark}
    </button>
  )
}

/**
 * The provenance spelled out under the badge, for anyone who does not hover:
 * which source produced the number, how confident the pipeline is, and the
 * amperage and phase count when they are known.
 */
export const VoltageProvenanceNote = ({ voltage }: { voltage: VoltageInfo | undefined }) => {
  const strings = useStrings()
  const locale = useLocale()
  const display = toVoltageDisplay(voltage)

  if (display.source === null)
    return <p className="ev-volt-note ev-volt-note--unknown">{strings.voltage.unknownHint}</p>

  const facts: string[] = [strings.voltage.sourceHint[display.source]]
  if (display.confidence !== null)
    facts.push(
      `${strings.voltage.confidenceLabel}: ${strings.voltage.confidence[display.confidence]}`,
    )
  if (display.amps !== null)
    facts.push(
      `${strings.voltage.amps}: ${formatNumber(display.amps, locale)} ${strings.voltage.ampUnit}`,
    )
  if (display.phases !== null) facts.push(strings.voltage.phasesValue(display.phases))

  return <p className="ev-volt-note">{facts.join(' · ')}</p>
}

/** Source, confidence and amperage for one reading, in the order they matter. */
const readingFacts = (reading: ReadingDisplay, strings: Strings, locale: string): string => {
  const facts: string[] = []
  if (reading.source !== null) facts.push(strings.voltage.source[reading.source])
  if (reading.confidence !== null)
    facts.push(
      `${strings.voltage.confidenceLabel}: ${strings.voltage.confidence[reading.confidence]}`,
    )
  if (reading.amps !== null)
    facts.push(`${formatNumber(reading.amps, locale)} ${strings.voltage.ampUnit}`)
  return facts.join(' · ')
}

const ReadingRow = ({ reading, shown }: { reading: ReadingDisplay; shown: boolean }) => {
  const strings = useStrings()
  const locale = useLocale()
  if (reading.figure === null) return null

  return (
    <li className={`ev-conflict__item${shown ? ' ev-conflict__item--shown' : ''}`}>
      <span className="ev-conflict__value">{figureText(reading.figure, strings, locale)}</span>
      <span className="ev-conflict__meta">
        {readingFacts(reading, strings, locale)}
        {shown && ` · ${strings.voltage.conflictShown}`}
      </span>
    </li>
  )
}

/**
 * Every reading anybody published for this connector, when they do not agree.
 *
 * The point of the block is that the reader, not the pipeline, gets to weigh the
 * disagreement: both figures are printed at the same size, each labelled with
 * the source that published it and how firmly that source's record is tied to
 * *this* socket. The published figure is marked as the one on the badge rather
 * than presented as the correct one, because the precedence that picked it is a
 * rule about evidence, not a finding about the charger.
 *
 * Renders nothing at all - not an empty box, not a "no conflicts" line - for the
 * overwhelming majority of connectors, where the sources agree or only one spoke.
 */
export const VoltageConflictNote = ({ connector }: { connector: Connector }) => {
  const strings = useStrings()
  const locale = useLocale()
  const display = toVoltageDisplay(connector.voltage)
  const { disputedPower } = connector

  if (display.alternatives.length === 0 && disputedPower === undefined) return null

  return (
    <div className="ev-conflict">
      <p className="ev-conflict__title">{strings.voltage.conflictTitle}</p>

      {display.alternatives.length > 0 && (
        <>
          <p className="ev-conflict__field">{strings.voltage.heading}</p>
          <ul className="ev-conflict__list">
            <ReadingRow reading={display} shown />
            {display.alternatives.map((reading) => (
              <ReadingRow
                key={`${reading.source}-${JSON.stringify(reading.figure)}`}
                reading={reading}
                shown={false}
              />
            ))}
          </ul>
        </>
      )}

      {disputedPower !== undefined && (
        <>
          <p className="ev-conflict__field">{strings.detail.power}</p>
          <ul className="ev-conflict__list">
            <li className="ev-conflict__item ev-conflict__item--shown">
              <span className="ev-conflict__value">{formatPower(connector.powerKw, locale)}</span>
              <span className="ev-conflict__meta">
                {`${strings.voltage.registrySource} · ${strings.voltage.conflictShown}`}
              </span>
            </li>
            <li className="ev-conflict__item">
              <span className="ev-conflict__value">
                {formatPower(disputedPower.powerKw, locale)}
              </span>
              <span className="ev-conflict__meta">
                {`${strings.voltage.source[disputedPower.source]} · ${strings.voltage.confidenceLabel}: ${strings.voltage.confidence[disputedPower.confidence]}`}
              </span>
            </li>
          </ul>
        </>
      )}

      <p className="ev-conflict__hint">{strings.voltage.conflictHint}</p>
    </div>
  )
}
